import type { CreateOrganisationOperatorData } from '@/api/resources/organisation-operators'
import { useForm } from '@tanstack/react-form'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { toast } from 'sonner'
import { z } from 'zod/v4'
import { machinesQueryOptions } from '@/api/resources/machines'
import { createOrganisationOperator } from '@/api/resources/organisation-operators'
import { organisationsQueryOptions } from '@/api/resources/organisations'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { AddOperatorForm } from '@/components/users/add-operator-form'
import { useServerErrors } from '@/hooks/use-server-errors'
import { queryKeys } from '@/lib/query-keys'
import { useOrganisationFilterStore } from '@/stores/organisation-filter-store'

const createOperatorSchema = z.object({
  first_name: z.string().min(1, 'First name is required'),
  last_name: z.string().min(1, 'Last name is required'),
  email: z.email('Please enter a valid email address'),
  email_optin: z.boolean().optional(),
  machine_ids: z.array(z.number()).min(1, 'Please select at least one machine'),
})

interface AddOperatorDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
}

export function AddOperatorDialog({
  open,
  onOpenChange,
}: AddOperatorDialogProps): React.ReactElement {
  const queryClient = useQueryClient()
  const { serverErrors, handleMutationError, clearServerErrors } = useServerErrors()

  const { data: organisations } = useQuery(organisationsQueryOptions())
  const selectedFilterIds = useOrganisationFilterStore((s) => s.selectedOrganisationIds)

  const [selectedOrganisationId, setSelectedOrganisationId] = useState<string | undefined>(
    undefined,
  )

  const organisationId: number | undefined = (() => {
    if (organisations?.length === 1) return organisations[0]?.id
    if (selectedFilterIds.length === 1) return selectedFilterIds[0]
    if (selectedOrganisationId !== undefined) return Number(selectedOrganisationId)
    return undefined
  })()

  const needsOrgSelect = (organisations?.length ?? 0) > 1 && selectedFilterIds.length !== 1

  const { data: machines } = useQuery({
    ...machinesQueryOptions(
      organisationId !== undefined ? { organisation_id: [organisationId] } : undefined,
    ),
    enabled: organisationId !== undefined,
  })

  const createMutation = useMutation({
    mutationFn: async (data: CreateOrganisationOperatorData) => {
      if (organisationId === undefined) throw new Error('No organisation selected')
      return createOrganisationOperator(organisationId, data)
    },
    onSuccess: () => {
      clearServerErrors()
      if (organisationId !== undefined) {
        void queryClient.invalidateQueries({
          queryKey: queryKeys.organisationOperators.all(organisationId),
        })
      }
      toast.success('Operator created successfully')
      onOpenChange(false)
    },
    onError: handleMutationError,
  })

  const form = useForm({
    defaultValues: {
      first_name: '',
      last_name: '',
      email: '',
      email_optin: false,
      machine_ids: [],
    } as CreateOrganisationOperatorData,
    validators: { onSubmit: createOperatorSchema },
    onSubmit: ({ value }) => createMutation.mutate(value),
  })

  function handleOrganisationChange(value: string): void {
    setSelectedOrganisationId(value)
    form.setFieldValue('machine_ids', [])
  }

  function handleSubmit(e: React.FormEvent): void {
    e.preventDefault()
    clearServerErrors()
    createMutation.reset()
    void form.handleSubmit()
  }

  function handleOpenChange(nextOpen: boolean): void {
    if (!nextOpen) {
      form.reset()
      clearServerErrors()
      setSelectedOrganisationId(undefined)
    }
    onOpenChange(nextOpen)
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Add operator</DialogTitle>
          <DialogDescription>
            Create a new operator and assign them to one or more machines.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <AddOperatorForm
            form={form}
            machines={organisationId !== undefined ? machines : undefined}
            serverErrors={serverErrors}
            organisations={needsOrgSelect ? organisations : undefined}
            selectedOrganisationId={selectedOrganisationId}
            onOrganisationChange={handleOrganisationChange}
          />

          <DialogFooter>
            <DialogClose asChild>
              <Button type="button" variant="outline">
                Cancel
              </Button>
            </DialogClose>
            <Button
              type="submit"
              disabled={createMutation.isPending || organisationId === undefined}
            >
              {createMutation.isPending ? 'Creating...' : 'Create'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
